import React, { useState, useEffect } from 'react';
import './OverallDashboard.css';
import { loadMonthData } from '../utils/storage';
import { COUNTRIES } from './CountrySelector';

const OverallDashboard = () => {
  const [overallStats, setOverallStats] = useState({
    totalViajes: 0,
    totalTransportes: 0,
    totalAlojamientos: 0,
    gastoRealTotal: 0,
    paisesUnicos: [],
    añosConDatos: [],
    viajesPorAño: {},
    gastoPorAño: {}
  });

  useEffect(() => {
    calculateOverallStats();
    const interval = setInterval(calculateOverallStats, 2000);
    return () => clearInterval(interval);
  }, []);

  const calculateOverallStats = () => {
    let totalViajes = 0;
    let totalTransportes = 0;
    let totalAlojamientos = 0;
    let gastoRealTotal = 0;
    const paisesSet = new Set();
    const añosConDatos = new Set();
    const viajesPorAño = {};
    const gastoPorAño = {};

    // Revisar todos los años desde 2020 hasta 2030
    for (let year = 2020; year <= 2030; year++) {
      let yearViajes = 0;
      let yearGasto = 0;
      let yearHasData = false;

      for (let month = 0; month < 12; month++) {
        const monthData = loadMonthData(month, year);
        
        if (monthData?.plan?.viajes) {
          const viajes = monthData.plan.viajes;
          yearViajes += viajes.length;
          
          if (viajes.length > 0) yearHasData = true;
          
          viajes.forEach(viaje => {
            totalViajes++;
            
            if (viaje.pais) {
              paisesSet.add(viaje.pais);
            }
            
            const transportes = viaje.transportes || (viaje.vuelos ? viaje.vuelos.map(v => ({ tipo: 'vuelo', ...v })) : []);
            if (transportes && transportes.length > 0) {
              totalTransportes += transportes.length;
            }
            
            if (viaje.airbnbs) {
              totalAlojamientos += viaje.airbnbs.length;
            }
          });
        }
        
        if (monthData?.reflection?.presupuesto?.items) {
          monthData.reflection.presupuesto.items.forEach(item => {
            if (item.real) {
              const real = parseFloat(item.real.toString().replace(/[^0-9.,-]/g, '').replace(',', '.')) || 0;
              gastoRealTotal += real;
              yearGasto += real;
            }
          });
        }
      }

      if (yearHasData || yearGasto > 0) {
        añosConDatos.add(year);
        viajesPorAño[year] = yearViajes;
        gastoPorAño[year] = yearGasto;
      }
    }

    const paisesInfo = Array.from(paisesSet)
      .map(code => COUNTRIES.find(c => c.code === code))
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name, 'es'));

    setOverallStats({
      totalViajes,
      totalTransportes,
      totalAlojamientos,
      gastoRealTotal,
      paisesUnicos: paisesInfo,
      añosConDatos: Array.from(añosConDatos).sort(),
      viajesPorAño,
      gastoPorAño
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getMaxViajes = () => {
    return Math.max(...Object.values(overallStats.viajesPorAño), 0);
  };

  const getMaxGasto = () => {
    return Math.max(...Object.values(overallStats.gastoPorAño), 0);
  };

  return (
    <div className="overall-dashboard-container">
      <header className="overall-header">
        <h1 className="overall-title">Dashboard General</h1>
        <p className="overall-subtitle">Estadísticas de todos tus años de viajes</p>
      </header>

      <div className="overall-content">
        {/* Estadísticas principales */}
        <section className="overall-stats-grid">
          <div className="overall-stat-card">
            <div className="overall-stat-icon">✈️</div>
            <div className="overall-stat-content">
              <h3 className="overall-stat-label">Total de Viajes</h3>
              <p className="overall-stat-value overall-stat-value-responsive">{overallStats.totalViajes}</p>
              <p className="overall-stat-description">En {overallStats.añosConDatos.length} {overallStats.añosConDatos.length === 1 ? 'año' : 'años'}</p>
            </div>
          </div>

          <div className="overall-stat-card">
            <div className="overall-stat-icon">🛫</div>
            <div className="overall-stat-content">
              <h3 className="overall-stat-label">Total Transportes</h3>
              <p className="overall-stat-value overall-stat-value-responsive">{overallStats.totalTransportes}</p>
              <p className="overall-stat-description">Vuelos, trenes y buses</p>
            </div>
          </div>

          <div className="overall-stat-card">
            <div className="overall-stat-icon">🏠</div>
            <div className="overall-stat-content">
              <h3 className="overall-stat-label">Total Alojamientos</h3>
              <p className="overall-stat-value overall-stat-value-responsive">{overallStats.totalAlojamientos}</p>
              <p className="overall-stat-description">Reservas totales</p>
            </div>
          </div>

          <div className="overall-stat-card overall-stat-card-gasto">
            <div className="overall-stat-icon">💸</div>
            <div className="overall-stat-content">
              <h3 className="overall-stat-label">Gasto Total</h3>
              <p className="overall-stat-value overall-stat-value-gasto">{formatCurrency(overallStats.gastoRealTotal)}</p>
              <p className="overall-stat-description">En todos los años</p>
            </div>
          </div>

          <div className="overall-stat-card">
            <div className="overall-stat-icon">🌍</div>
            <div className="overall-stat-content">
              <h3 className="overall-stat-label">Países Visitados</h3>
              <p className="overall-stat-value overall-stat-value-responsive">{overallStats.paisesUnicos.length}</p>
              <p className="overall-stat-description">Países únicos</p>
            </div>
          </div>
        </section>

        {/* Países visitados */}
        {overallStats.paisesUnicos.length > 0 && (
          <section className="overall-countries-section">
            <h2 className="overall-section-title">Todos los Países Visitados</h2>
            <div className="overall-countries-grid">
              {overallStats.paisesUnicos.map((pais, index) => (
                <div key={pais.code} className="overall-country-card" style={{ animationDelay: `${index * 0.03}s` }}>
                  <div className="overall-country-flag">{pais.flag}</div>
                  <div className="overall-country-name">{pais.name}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Gráfico de viajes por año */}
        {overallStats.añosConDatos.length > 0 && (
          <section className="overall-chart-section">
            <h2 className="overall-section-title">Viajes por Año</h2>
            <div className="overall-chart-container">
              {overallStats.añosConDatos.map(year => {
                const viajes = overallStats.viajesPorAño[year] || 0;
                const maxViajes = getMaxViajes();
                const percentage = maxViajes > 0 ? (viajes / maxViajes) * 100 : 0;
                
                return (
                  <div key={year} className="overall-chart-bar-container">
                    <div className="overall-chart-bar-label">{year}</div>
                    <div className="overall-chart-bar-wrapper">
                      <div 
                        className="overall-chart-bar"
                        style={{ width: `${percentage}%` }}
                      >
                        <span className="overall-chart-bar-value">{viajes}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Gráfico de gasto por año */}
        {overallStats.añosConDatos.length > 0 && (
          <section className="overall-chart-section">
            <h2 className="overall-section-title">Gasto por Año</h2>
            <div className="overall-chart-container">
              {overallStats.añosConDatos.map(year => {
                const gasto = overallStats.gastoPorAño[year] || 0;
                const maxGasto = getMaxGasto();
                const percentage = maxGasto > 0 ? (gasto / maxGasto) * 100 : 0;
                
                return (
                  <div key={year} className="overall-chart-bar-container">
                    <div className="overall-chart-bar-label">{year}</div>
                    <div className="overall-chart-bar-wrapper">
                      <div 
                        className="overall-chart-bar overall-chart-bar-gasto"
                        style={{ width: `${percentage}%` }}
                      >
                        <span className="overall-chart-bar-value">{formatCurrency(gasto)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default OverallDashboard;

